import { HighlightOff } from "@mui/icons-material"
import EastIcon from "@mui/icons-material/East"
import { Divider } from "@mui/material"
import isEmpty from "lodash/isEmpty"
import { ReactNode, useEffect, useState } from "react"

import { uuid } from "uuidv4"

import { Button } from "src/components/common/button"
import { Drawer } from "src/components/common/drawer"
import { IconButton } from "src/components/common/iconButton"
import { BasicLayoutStyleProps } from "src/components/layouts/basic"
import { create } from "src/helpers/bem"
import { defaultIncident } from "src/helpers/defaultIncident"
import { postBlob } from "src/queriesXRM/file"
import {
  fetchWebshopIncidents,
  postClaimIncident,
  postIncidentAuthorization,
  postIncidentCancellation,
} from "src/queriesXRM/incident"
import { useAccount } from "src/states/account"
import { useBooking } from "src/states/bookingDetail"
import { Dialogs, mergeDialog, useDialog } from "src/states/dialogs"
import { useIncidents } from "src/states/incident"
import { addNotification } from "src/states/notifications"
import { TranslationMessages } from "src/translations"

import { xRMApiIncident, xRMApiIncidentType } from "src/types/xRM"

import AuthorizationRequestFields, {
  ClientLocation,
} from "./authorization-request-fields/AuthorizationRequestFields"
import CancellationFields from "./cancellation-fields/CancellationFields"
import styles from "./GetSupportDialog.module.scss"
import IncidentTypeSelect from "./IncidentTypeSelect"
import { InfoFields } from "./info-fields/InfoFields"

const bem = create(styles, "GetSupportDialog")

export type ModifiedIncident = Omit<xRMApiIncident, "orderTypes"> & {
  orderTypes?: number[]
}

type GetSupportDialogProps = {
  messages: TranslationMessages
  style: BasicLayoutStyleProps
}

export const GetSupportDialog = ({
  messages,
  style,
}: GetSupportDialogProps) => {
  const { booking } = useBooking()
  const { incidents } = useIncidents()
  const [files, setFiles] = useState<File[]>([])
  const { firstName, lastName, email } = useAccount()
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const {
    open,
    disabled: isReadOnly,
    incidentId,
  } = useDialog("incidentDialog") as Dialogs["incidentDialog"]
  const [incident, setIncident] = useState<ModifiedIncident>(defaultIncident)

  const {
    companyName,
    companyStreet,
    companyZipCode,
    companyCity,
    companyCountryCodeId,
    companyEmail,
    companyPhone,
    description,
    orderTypes,
    incidentPositions,
    referenceRecordId,
    incidentType,
    language,
    companyClientLocation,
    clientVatId,
  } = incident
  const dialogMessage =
    messages.pages.account.overview.profile.incidents.createDialog

  const incidentFields: Record<xRMApiIncidentType, ReactNode> = {
    Claim: (
      <InfoFields
        incident={incident}
        messages={messages}
        setIncident={setIncident}
      />
    ),
    Cancellation: (
      <CancellationFields
        incident={incident}
        messages={messages}
        setIncident={setIncident}
      />
    ),
    AuthorizationRequest: (
      <AuthorizationRequestFields
        files={files}
        incident={incident}
        setFiles={setFiles}
        setIncident={setIncident}
      />
    ),
  }

  const submitButtonDisabled: Record<xRMApiIncidentType, boolean> = {
    Claim: isEmpty(orderTypes) || !description || description === "",
    Cancellation:
      !description ||
      description === "" ||
      !referenceRecordId ||
      isEmpty(incidentPositions),
    AuthorizationRequest:
      [
        companyName,
        companyStreet,
        companyZipCode,
        companyCity,
        companyCountryCodeId,
        companyEmail,
        companyPhone,
        companyClientLocation?.toString(),
      ].some((value) => !value || value.trim() === "") ||
      (companyClientLocation === ClientLocation.ComanpyInEu &&
        (!clientVatId || clientVatId === "")),
  }

  const handleCreateIncident = async () => {
    const mappedIncident: xRMApiIncident = {
      ...incident,
      title: `${booking?.exhibition?.name} - ${firstName} ${lastName}`,
      orderTypes:
        isEmpty(orderTypes) || !orderTypes ? null : orderTypes.join(","),
    }

    try {
      setIsCreating(true)
      if (incidentType === "AuthorizationRequest") {
        const result = await postIncidentAuthorization({
          companyCity,
          companyEmail,
          companyStreet,
          companyCountryCodeId,
          companyZipCode,
          language,
          email,
          firstName,
          lastName,
          phone: companyPhone,
          authorizationIncident: { ...mappedIncident, id: uuid() },
        })
        if (!isEmpty(files)) {
          for (const file of files) {
            console.log(file.name)
            try {
              await postBlob(
                `authorization-requests`,
                file,
                `${result.data.id}/${file.name}`,
                {
                  category: "Account",
                  incidentId: result.data.id,
                },
                "Webshop",
              )
            } catch (error) {
              addNotification({
                autoclose: true,
                variant: "error",
                content:
                  messages.pages.account.overview.profile.incidents.createDialog
                    .authorizationRequestFields.fileUpload.error,
              })
            }
          }
        }
      } else if (incidentType === "Cancellation") {
        await postIncidentCancellation(mappedIncident)
      } else {
        await postClaimIncident(mappedIncident)
      }
      fetchWebshopIncidents()
      mergeDialog("incidentDialog", { open: false })
      addNotification({
        autoclose: true,
        variant: "success",
        content: dialogMessage.notifications.success,
      })
      setIncident(defaultIncident)
    } catch (error) {
      console.error(error)
      addNotification({
        autoclose: true,
        variant: "error",
        content: dialogMessage.notifications.error,
      })
    } finally {
      setFiles([])
      setIsCreating(false)
    }
  }

  const onCloseDialog = () => {
    if (isReadOnly) {
      mergeDialog("incidentDialog", {
        open: false,
        disabled: false,
        incidentId: undefined,
      })
    }
    setFiles([])
    setIncident(defaultIncident)
  }

  useEffect(() => {
    if (incidentId) {
      const selectedIncident = incidents.find((i) => i.id === incidentId)
      if (selectedIncident) {
        const selectedIncidentCopy = { ...selectedIncident } as ModifiedIncident
        setIncident(selectedIncidentCopy)
      }
    }
  }, [incidentId])

  return (
    <Drawer
      anchor="right"
      open={open}
      style={style}
      sx={{ height: "100%" }}
      onClose={onCloseDialog}
      onOpen={() => mergeDialog("incidentDialog", { open: true })}
    >
      <div className={bem()}>
        <div className={bem("header")}>
          <h2>{dialogMessage.title}</h2>
          {isReadOnly && (
            <div className={bem("close")}>
              <IconButton
                color={"primary"}
                icon={HighlightOff}
                onClick={onCloseDialog}
              />
            </div>
          )}
        </div>

        <IncidentTypeSelect
          incident={incident}
          messages={messages}
          setIncident={setIncident}
        />

        <Divider className={bem("divider")} />

        {incidentType && incidentFields[incidentType]}

        {!isReadOnly && (
          <div className={bem("footer")}>
            <div className={bem("footer__buttons")}>
              <Button
                buttonType="secondary"
                disabled={isCreating}
                title={dialogMessage.buttons.cancel}
                variant="outlined"
                onClick={async () => {
                  mergeDialog("incidentDialog", {
                    open: false,
                    incidentId: undefined,
                  })
                  setIncident(defaultIncident)
                  setFiles([])
                }}
              />
              <Button
                buttonType="primary"
                disabled={!incidentType || submitButtonDisabled[incidentType]}
                endIcon={<EastIcon fontSize="small" />}
                loading={isCreating}
                title={dialogMessage.buttons.createIncident}
                variant="outlined"
                onClick={handleCreateIncident}
              />
            </div>
          </div>
        )}
      </div>
    </Drawer>
  )
}

export default GetSupportDialog
